import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { toast } from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useAuth } from "../../../context/AuthContext";
import { useChat } from "../../../context/ChatContext";
import { useSocket } from "../../../context/SocketContext";
import { storeChats, uploadFile } from "../../../services/chatServices";
import { sendChannelMessage } from "../../../services/channelService";
import { useDraft } from "../hooks/useDraft";
import DraftIndicator from "./DraftIndicator";

import EmojiPickerLib from "emoji-picker-react";

// ── Emoji Picker — portal-based so it escapes all overflow/stacking contexts ─
function EmojiPicker({ anchorRef, onSelect, onClose, pickerRef }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    // Position above the button, right-aligned
    setCoords({
      top: rect.top + window.scrollY - 400, // 400 ≈ picker height + gap
      left: Math.max(8, rect.right + window.scrollX - 320), // 320 = picker width
    });
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      ref={pickerRef}
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        zIndex: 9999,
      }}
    >
      <EmojiPickerLib
        onEmojiClick={(emojiData) => {
          onSelect(emojiData.emoji);
          onClose();
        }}
        autoFocusSearch
        lazyLoadEmojis
        skinTonesDisabled
        height={380}
        width={320}
        searchPlaceholder="Search emoji..."
        previewConfig={{ showPreview: false }}
      />
    </div>,
    document.body,
  );
}

// ── Link Modal ───────────────────────────────────────────────────────────────
function LinkModal({ selectedText, onInsert, onClose }) {
  const [url, setUrl] = useState("https://");
  const [text, setText] = useState(selectedText || "");

  const handleInsert = () => {
    if (!url.trim()) return;
    onInsert(`[${text || url}](${url})`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-sm bg-app-surface border border-border-subtle rounded-3xl shadow-modal p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-widest">
            Insert link
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-black/5 text-text-muted hover:text-text-primary transition-all"
          >
            <AppIcon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted block mb-1.5 opacity-60">
              Text to display
            </label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Link text"
              className="w-full bg-app-bg border border-border-subtle rounded-xl px-4 py-3 text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all shadow-subtle"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text-muted block mb-1.5 opacity-60">
              Link URL
            </label>
            <input
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInsert();
                if (e.key === "Escape") onClose();
              }}
              placeholder="https://example.com"
              className="w-full bg-app-bg border border-border-subtle rounded-xl px-4 py-3 text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/5 transition-all shadow-subtle"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-text-muted hover:text-text-primary hover:bg-bg-muted transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={!url.trim() || url === "https://"}
              className={`flex-1 py-3 rounded-xl text-[13px] font-bold shadow-premium transition-all ${
                url.trim() && url !== "https://"
                  ? "bg-primary text-white hover:bg-primary-dark shadow-primary/20"
                  : "bg-black/5 text-text-muted cursor-not-allowed opacity-50"
              }`}
            >
              Add link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mention List Component ───────────────────────────────────────────────────
function MentionDropdown({
  filteredMembers = [],
  onSelect,
  dropdownRef,
  selectedIndex,
  itemRefs,
  channelMembers,
}) {
  if (filteredMembers.length === 0) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute bottom-full mb-2 left-0 z-50 w-64 rounded-2xl bg-app-surface border border-border-subtle shadow-modal overflow-hidden animate-fade-in"
    >
      <div className="px-3 py-2 border-b border-border-subtle">
        <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
          Mention someone
        </p>
      </div>
      {filteredMembers.map((member, i) => (
        <button
          key={member._id || member.id || i}
          ref={(el) => (itemRefs.current[i] = el)}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(member);
          }}
          className={`w-full flex items-center gap-3 px-3 py-3 transition-colors text-left ${i === selectedIndex ? "bg-primary/10 text-primary" : "hover:bg-black/5"}`}
        >
          {member.isChannelNotify ? (
            <>
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <AppIcon
                  name="MegaphoneIcon"
                  size={18}
                  className="text-primary"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-text-primary truncate">
                  {member.name}
                </p>
                <p className="text-[11px] text-text-muted truncate">
                  @channel &middot; Notifies all {channelMembers.length} members
                </p>
              </div>
            </>
          ) : (
            <>
              <img
                src={
                  member.profilePhoto ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name || "U")}&background=random`
                }
                className="w-9 h-9 rounded-xl object-cover border border-border-subtle shadow-subtle"
                alt={member.name}
              />
              <div className="min-w-0">
                <p className="text-sm font-bold text-text-primary truncate">
                  {member.name || member.displayName}
                </p>
                {member.username && (
                  <p className="text-[11px] text-text-muted truncate">
                    @{member.username}
                  </p>
                )}
              </div>
            </>
          )}
        </button>
      ))}
      <div className="flex items-center justify-between px-3 py-2 border-t border-border-subtle bg-black/[0.02]">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-[10px] text-text-muted opacity-60">
            <kbd className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-black/5 border border-border-subtle text-[10px] font-bold text-text-muted shadow-subtle">
              ↑
            </kbd>
            <kbd className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-black/5 border border-border-subtle text-[10px] font-bold text-text-muted shadow-subtle">
              ↓
            </kbd>
            <span>Navigate</span>
          </span>
          <span className="flex items-center gap-1 text-[10px] text-text-muted opacity-60">
            <kbd className="inline-flex items-center justify-center px-1.5 h-5 rounded-md bg-black/5 border border-border-subtle text-[10px] font-bold text-text-muted shadow-subtle">
              ↵
            </kbd>
            <span>Select</span>
          </span>
        </div>
        <span className="flex items-center gap-1 text-[10px] text-text-muted opacity-60">
          <kbd className="inline-flex items-center justify-center px-1.5 h-5 rounded-md bg-black/5 border border-border-subtle text-[10px] font-bold text-text-muted shadow-subtle">
            esc
          </kbd>
          <span>Close</span>
        </span>
      </div>
    </div>
  );
}

// ── Attachment Preview Grid ──────────────────────────────────────────────────
const FILE_ICON_MAP = {
  pdf: "DocumentTextIcon",
  doc: "DocumentTextIcon",
  docx: "DocumentTextIcon",
  xls: "TableCellsIcon",
  xlsx: "TableCellsIcon",
  zip: "ArchiveBoxIcon",
  rar: "ArchiveBoxIcon",
  "7z": "ArchiveBoxIcon",
  mp4: "VideoCameraIcon",
  mov: "VideoCameraIcon",
  webm: "VideoCameraIcon",
  mp3: "MusicalNoteIcon",
  wav: "MusicalNoteIcon",
  ogg: "MusicalNoteIcon",
  txt: "DocumentIcon",
  json: "CodeBracketIcon",
  js: "CodeBracketIcon",
  ts: "CodeBracketIcon",
  jsx: "CodeBracketIcon",
  tsx: "CodeBracketIcon",
  default: "DocumentIcon",
};

function getDocIcon(filename) {
  const ext = filename?.split(".").pop()?.toLowerCase() || "";
  return FILE_ICON_MAP[ext] || FILE_ICON_MAP.default;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Single image thumbnail cell

function ImageThumb({ file, index, onRemove }) {
  return (
    <div
      className="relative group flex-shrink-0 rounded-xl bg-black/5"
      style={{ width: 80, height: 80 }}
    >
      <div className="absolute inset-0 overflow-hidden rounded-xl">
        <img
          src={file.previewUrl}
          alt={file.name}
          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
          loading="lazy"
          draggable={false}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-200 rounded-xl pointer-events-none" />
      </div>
      <RemoveButton onClick={() => onRemove(index)} />
    </div>
  );
}

// Single non-image file card
function FileCard({ file, index, onRemove }) {
  return (
    <div
      className="relative group flex-shrink-0 flex items-center gap-2 px-2.5 py-2 bg-app-bg border border-border-subtle rounded-xl shadow-subtle"
      style={{ width: 160, maxWidth: 160 }}
    >
      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
        <AppIcon name={getDocIcon(file.name)} size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-text-primary truncate leading-tight">
          {file.name}
        </p>
        <p className="text-[9px] text-text-muted font-medium uppercase tracking-wider mt-0.5">
          {formatBytes(file.size)}
        </p>
      </div>
      <RemoveButton onClick={() => onRemove(index)} />
    </div>
  );
}

// AFTER
function RemoveButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute -top-1.5 -right-1.5 z-10 w-5 h-5 bg-white border border-border-subtle rounded-full flex items-center justify-center text-text-muted hover:text-white hover:bg-red-500 hover:border-red-500 shadow-md transition-all duration-150"
      title="Remove"
      type="button"
    >
      <AppIcon name="XMarkIcon" size={10} />
    </button>
  );
}

function AttachmentPreviewGrid({ files, onRemove }) {
  const MAX_VISIBLE = 8;
  const visible = files.slice(0, MAX_VISIBLE);
  const overflow = files.length - MAX_VISIBLE;

  return (
    <div className="mx-3 mt-2 mb-1 animate-fade-in">
      <div className="flex flex-wrap gap-2">
        {visible.map((file, i) => {
          const originalIndex = files.indexOf(file);
          if (file.isImage) {
            return (
              <ImageThumb
                key={originalIndex}
                file={file}
                index={originalIndex}
                onRemove={onRemove}
              />
            );
          }
          return (
            <FileCard
              key={originalIndex}
              file={file}
              index={originalIndex}
              onRemove={onRemove}
            />
          );
        })}
        {/* +N overflow badge */}
        {overflow > 0 && (
          <div
            className="flex-shrink-0 flex items-center justify-center rounded-xl bg-black/10 text-text-muted text-[12px] font-bold"
            style={{ width: 80, height: 80 }}
          >
            +{overflow}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main MessageInput ────────────────────────────────────────────────────────
export default function MessageInput({
  channelName,
  onMessagesUpdate,
  channelMembers = [],
  isNearBottom,
  scrollToBottom,
}) {
  const { user } = useAuth();
  const { activeChat, updateChatDraftStatus, refetchDrafts } = useChat();
  const socket = useSocket();
  const userId = user?._id || user?.id;
  const refetchDraftsRef = useRef(refetchDrafts);
  useEffect(() => {
    refetchDraftsRef.current = refetchDrafts;
  }, [refetchDrafts]);

  const chatId = activeChat?.data?._id || activeChat?.data?.id;
  const chatType = activeChat?.type;

  const {
    value,
    setValue: setDraftValue,
    saveDraft,
    clearDraft,
    lastSaved,
    hasUnsavedChanges,
    isLoading: draftLoading,
    scheduledAt,
    setScheduled,
    clearScheduled,
    suppressAutoSave,
  } = useDraft(chatId, chatType, "");

  const [isCode, setIsCode] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop()?.toLowerCase();
    const iconMap = {
      pdf: "DocumentTextIcon",
      doc: "DocumentTextIcon",
      docx: "DocumentTextIcon",
      xls: "TableCellsIcon",
      xlsx: "TableCellsIcon",
      zip: "ArchiveBoxIcon",
      rar: "ArchiveBoxIcon",
      jpg: "PhotoIcon",
      jpeg: "PhotoIcon",
      png: "PhotoIcon",
      gif: "PhotoIcon",
      webp: "PhotoIcon",
      mp4: "VideoCameraIcon",
      mov: "VideoCameraIcon",
      webm: "VideoCameraIcon",
      mp3: "MusicalNoteIcon",
      default: "DocumentIcon",
    };
    return iconMap[ext] || iconMap.default;
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionStartIdx, setMentionStartIdx] = useState(-1);
  const mentionQueryRef = useRef(null);
  const mentionStartIdxRef = useRef(-1);
  const mentionBaseValueRef = useRef("");
  const mentionSelectingRef = useRef(false);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const mentionItemRefs = useRef([]);

  const filteredMembers = useMemo(() => {
    if (mentionQuery === null) return [];

    const memberMatches = channelMembers
      .filter(
        (m) =>
          m.name?.toLowerCase().includes(mentionQuery.toLowerCase()) ||
          m.username?.toLowerCase().includes(mentionQuery.toLowerCase()),
      )
      .slice(0, 6);

    if (activeChat?.type === "channel") {
      const channelOption = {
        _id: "@channel",
        name: "Notify Everyone",
        username: "channel",
        isChannelNotify: true,
      };
      return [channelOption, ...memberMatches].slice(0, 7);
    }

    return memberMatches;
  }, [mentionQuery, channelMembers, activeChat?.type]);

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e) => {
      if (emojiPickerRef.current && emojiPickerRef.current.contains(e.target))
        return;
      if (emojiBtnRef.current && emojiBtnRef.current.contains(e.target)) return;
      setShowEmojiPicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmojiPicker]);

  useEffect(() => {
    setMentionSelectedIndex(0);
    mentionItemRefs.current = [];
  }, [mentionQuery, channelMembers]);

  useEffect(() => {
    if (
      mentionSelectedIndex >= 0 &&
      mentionItemRefs.current[mentionSelectedIndex]
    ) {
      mentionItemRefs.current[mentionSelectedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [mentionSelectedIndex]);

  const getSelectionInfo = () => {
    const el = inputRef.current;
    if (!el) return { start: 0, end: 0, selected: "" };
    return {
      start: el.selectionStart,
      end: el.selectionEnd,
      selected: value.slice(el.selectionStart, el.selectionEnd),
    };
  };

  const insertAtCursor = useCallback(
    (before, after = "", replaceSelected = false) => {
      const el = inputRef.current;
      if (!el) return;
      const { start, end, selected } = getSelectionInfo();
      const inner = replaceSelected ? selected : selected;
      const newText =
        value.slice(0, start) + before + inner + after + value.slice(end);
      setDraftValue(newText);
      requestAnimationFrame(() => {
        el.focus();
        const cursor = start + before.length + inner.length + after.length;
        el.setSelectionRange(
          replaceSelected ? start + before.length : cursor,
          replaceSelected ? start + before.length + inner.length : cursor,
        );
      });
    },
    [value],
  );

  // ── Link ──────────────────────────────────────────────────────────────
  const handleLinkClick = () => {
    const { selected } = getSelectionInfo();
    setSelectedText(selected);
    setShowLinkModal(true);
  };

  const handleLinkInsert = (markdownLink) => {
    const el = inputRef.current;
    if (!el) return;
    const { start, end } = getSelectionInfo();
    const newText = value.slice(0, start) + markdownLink + value.slice(end);
    setDraftValue(newText);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + markdownLink.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  // ── Bullet List ───────────────────────────────────────────────────────
  const handleBulletList = () => {
    const el = inputRef.current;
    if (!el) return;
    const { start, end } = getSelectionInfo();
    const beforeCursor = value.slice(0, start);
    const afterCursor = value.slice(end);

    if (start !== end) {
      const selectedLines = value.slice(start, end).split("\n");
      const bulleted = selectedLines
        .map((line) => (line.startsWith("• ") ? line.slice(2) : `• ${line}`))
        .join("\n");
      const newText = beforeCursor + bulleted + afterCursor;
      setDraftValue(newText);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start, start + bulleted.length);
      });
      return;
    }

    const lineStart = beforeCursor.lastIndexOf("\n") + 1;
    const currentLine = value.slice(lineStart, end);

    if (currentLine.startsWith("• ")) {
      const newText =
        value.slice(0, lineStart) + currentLine.slice(2) + value.slice(end);
      setDraftValue(newText);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start - 2, start - 2);
      });
    } else {
      const newText = value.slice(0, lineStart) + "• " + value.slice(lineStart);
      setDraftValue(newText);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + 2, start + 2);
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (mentionQuery !== null) {
        e.preventDefault();
        const item = filteredMembers[mentionSelectedIndex];
        if (item) handleMentionSelect(item);
        return;
      }
      // ── Slack-style list awareness ──────────────────────────────────────
      const el = inputRef.current;
      const pos = el.selectionStart;
      const beforeCursor = value.slice(0, pos);
      const lineStart = beforeCursor.lastIndexOf("\n") + 1;
      const currentLine = value.slice(lineStart, pos);
      const bullet = /^(\s*)([-*•])\s+(.*)$/.exec(currentLine);
      const numbered = /^(\s*)(\d+)\.\s+(.*)$/.exec(currentLine);

      if (bullet || numbered) {
        const itemContent = bullet ? bullet[3] : numbered[3];
        // Empty item + Enter → exit the list (strip the marker), don't send
        if (itemContent.trim() === "") {
          e.preventDefault();
          const newValue = value.slice(0, lineStart) + value.slice(pos);
          setDraftValue(newValue);
          requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(lineStart, lineStart);
          });
          return;
        }
        // Non-empty item + Enter (or Shift+Enter) → continue the list
        e.preventDefault();
        if (bullet) {
          insertAtCursor(`\n${bullet[1]}${bullet[2]} `, "");
        } else {
          const next = parseInt(numbered[2], 10) + 1;
          insertAtCursor(`\n${numbered[1]}${next}. `, "");
        }
        return;
      }

      // Not a list line: plain Enter sends, Shift+Enter inserts a newline.
      if (!e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }

    if (e.key === "ArrowDown" && mentionQuery !== null) {
      e.preventDefault();
      setMentionSelectedIndex((prev) =>
        filteredMembers.length === 0
          ? 0
          : Math.min(prev + 1, filteredMembers.length - 1),
      );
    }

    if (e.key === "ArrowUp" && mentionQuery !== null) {
      e.preventDefault();
      setMentionSelectedIndex((prev) => Math.max(prev - 1, 0));
    }

    if (e.key === "Escape" && mentionQuery !== null) {
      setMentionQuery(null);
    }
  };

  const handleEmojiSelect = (emoji) => {
    const el = inputRef.current;
    if (!el) return;
    const { start, end } = getSelectionInfo();
    const newText = value.slice(0, start) + emoji + value.slice(end);
    setDraftValue(newText);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  const handleAtClick = () => {
    const el = inputRef.current;
    if (!el) return;
    if (mentionQuery !== null) {
      mentionQueryRef.current = null;
      mentionStartIdxRef.current = -1;
      mentionBaseValueRef.current = "";
      setMentionQuery(null);
      setMentionStartIdx(-1);
      return;
    }
    const { start } = getSelectionInfo();
    const currentVal = el.value;
    const newText = currentVal.slice(0, start) + "@" + currentVal.slice(start);
    mentionBaseValueRef.current = newText;
    mentionStartIdxRef.current = start + 1;
    mentionQueryRef.current = "";
    setMentionStartIdx(start + 1);
    setMentionQuery("");
    setDraftValue(newText);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + 1, start + 1);
    });
  };

  const handleMentionSelect = (member) => {
    if (mentionSelectingRef.current) return;
    mentionSelectingRef.current = true;

    const el = inputRef.current;
    if (!el) {
      mentionSelectingRef.current = false;
      return;
    }

    const base = mentionBaseValueRef.current;
    const startIdx = mentionStartIdxRef.current;
    const query = mentionQueryRef.current ?? "";

    const mention = member.isChannelNotify
      ? `@channel `
      : `@${member.username || member.name} `;
    const newText =
      base.slice(0, startIdx - 1) +
      mention +
      base.slice(startIdx + query.length);

    mentionQueryRef.current = null;
    mentionStartIdxRef.current = -1;
    mentionBaseValueRef.current = "";
    setMentionQuery(null);
    setMentionStartIdx(-1);
    setDraftValue(newText);

    const cursor = startIdx - 1 + mention.length;
    requestAnimationFrame(() => {
      mentionSelectingRef.current = false;
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  };

  const handleChange = (e) => {
    const newVal = e.target.value;

    if (mentionSelectingRef.current) return;

    setDraftValue(newVal);

    if (newVal.trim()) {
      emitTyping();
    }

    const cursor = e.target.selectionStart;
    const textUpToCursor = newVal.slice(0, cursor);
    const atMatch = textUpToCursor.match(/@(\w*)$/);
    if (atMatch) {
      const q = atMatch[1];
      const idx = cursor - q.length;
      mentionBaseValueRef.current = newVal;
      mentionQueryRef.current = q;
      mentionStartIdxRef.current = idx;
      setMentionQuery(q);
      setMentionStartIdx(idx);
    } else {
      mentionBaseValueRef.current = "";
      mentionQueryRef.current = null;
      mentionStartIdxRef.current = -1;
      setMentionQuery(null);
    }
  };

  const handleSend = async (overrideValue = null, overrideType = null) => {
    // 1. Process all pending files
    if (pendingFiles.length > 0 && activeChat && user) {
      const filesToSend = [...pendingFiles];
      setPendingFiles([]); // Clear immediately

      for (const file of filesToSend) {
        const fileUrl = file.url;
        const timestamp = new Date().toISOString();
        const tempId = `temp_${Date.now()}_${Math.random()}`;

        const optimisticMsg = {
          _id: tempId,
          __tempId: tempId,
          senderId: userId,
          sender: user.name,
          senderInfo: {
            _id: userId,
            name: user.name,
            profilePhoto: user.profilePhoto || user.profileImage || null,
            username: user.username || null,
          },
          senderAvatar: user.profilePhoto || user.profileImage || null,
          content: fileUrl,
          message: fileUrl,
          text: fileUrl,
          timestamp,
          type: "url",
          status: "sent",
        };
        onMessagesUpdate((prev) => [...prev, optimisticMsg]);

        try {
          if (activeChat.type === "channel") {
            const channelId = activeChat.data._id || activeChat.data.id;
            const reqPayload = {
              channel: channelId,
              sender: userId,
              text: fileUrl,
              type: "url",
              __tempId: tempId,
            };
            await sendChannelMessage(channelId, reqPayload);
          } else {
            const receiverId = activeChat.data._id || activeChat.data.id;
            const receiverName =
              activeChat.data.name || activeChat.data.displayName;
            const sortedIds = [userId, receiverId].sort();
            const reqPayload = {
              user1Id: sortedIds[0],
              user2Id: sortedIds[1],
              messages: [
                {
                  senderId: userId,
                  sender: user.name,
                  receiver: receiverName,
                  type: "url",
                  message: fileUrl,
                  timestamp,
                  __tempId: tempId,
                },
              ],
            };
            await storeChats(reqPayload);
          }
        } catch (error) {
          console.error("Error sending file:", error);
          onMessagesUpdate((prev) =>
            prev.map((m) =>
              m.__tempId === tempId ? { ...m, status: "error" } : m,
            ),
          );
        }
      }
      // CRITICAL: No 'return' here — allows text to also send if present
    }

    const messageToSend = overrideValue ?? value;
    const msgType = overrideType ?? (isCode ? "code" : "text");

    if (!messageToSend.trim() || !activeChat || !user) return;

    if (scheduledAt) {
      if (isScheduling) return;
      setIsScheduling(true);
      try {
        await saveDraft(messageToSend);
        await refetchDraftsRef.current();
        const scheduledDate = new Date(scheduledAt);
        toast.success(
          `Message scheduled for ${scheduledDate.toLocaleString()}`,
        );
        suppressAutoSave();
        setDraftValue("");
        setScheduled(null);
        setShowScheduler(false);
      } catch (error) {
        console.error(
          "[MessageInput] ❌ Error scheduling message:",
          error.message,
          { stack: error.stack },
        );
        toast.error("Failed to schedule message. Please try again.");
        return;
      } finally {
        setIsScheduling(false);
      }
      return;
    }

    const timestamp = new Date().toISOString();
    const tempId = `temp_${Date.now()}`;

    const optimisticMsg = {
      _id: tempId,
      __tempId: tempId,
      senderId: userId,
      sender: user.name,
      senderAvatar: user.profilePhoto || user.profileImage || null,
      senderInfo: {
        _id: userId,
        name: user.name,
        profilePhoto: user.profilePhoto || user.profileImage || null,
        username: user.username || null,
      },
      content: messageToSend,
      message: messageToSend,
      timestamp,
      type: msgType,
      status: "sent",
    };

    onMessagesUpdate((prev) => [...prev, optimisticMsg]);
    setDraftValue("");
    setIsCode(false);
    setShowScheduler(false);
    setMentionQuery(null);
    clearTimeout(typingTimeoutRef.current);
    if (socket && activeChat && user) {
      const targetId = activeChat.data._id || activeChat.data.id;
      if (activeChat.type === "dm") {
        socket.emit("stop-typing", {
          senderId: userId,
          receiverId: targetId,
          userName: user.name,
        });
      } else {
        socket.emit("channel-stop-typing", {
          channelId: targetId,
          senderId: userId,
          userName: user.name,
        });
      }
    }

    try {
      if (activeChat.type === "channel") {
        const channelId = activeChat.data._id || activeChat.data.id;
        const reqPayload = {
          channel: channelId,
          sender: userId,
          text: messageToSend,
          type: msgType,
          __tempId: tempId,
        };
        const response = await sendChannelMessage(channelId, reqPayload);
      } else {
        const receiverId = activeChat.data._id || activeChat.data.id;
        const receiverName =
          activeChat.data.name || activeChat.data.displayName;
        const timestamp = new Date().toISOString();

        const sortedIds = [userId, receiverId].sort();
        const user1Id = sortedIds[0];
        const user2Id = sortedIds[1];

        const reqPayload = {
          user1Id,
          user2Id,
          messages: [
            {
              senderId: userId,
              sender: user.name,
              receiver: receiverName,
              type: msgType,
              message: messageToSend,
              timestamp,
              __tempId: tempId,
            },
          ],
        };
        const response = await storeChats(reqPayload);
      }
      clearDraft();
    } catch (error) {
      console.error("Error sending message:", error);
      onMessagesUpdate((prev) =>
        prev.map((m) =>
          m.__tempId === tempId ? { ...m, status: "error" } : m,
        ),
      );
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !activeChat || !user) return;
    setIsUploading(true);

    // Create a local object URL immediately for instant preview
    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null;

    const expectedConversationId =
      activeChat.type === "channel"
        ? activeChat.data._id || activeChat.data.id
        : [user._id || user.id, activeChat.data._id || activeChat.data.id]
            .sort()
            .join("_");

    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await uploadFile(formData);
      if (response?.data) {
        const currentConversationId =
          activeChat.type === "channel"
            ? activeChat.data._id || activeChat.data.id
            : [user._id || user.id, activeChat.data._id || activeChat.data.id]
                .sort()
                .join("_");
        if (currentConversationId === expectedConversationId) {
          setPendingFiles((prev) => [
            ...prev,
            {
              url: response.data,
              previewUrl, // local blob URL for thumbnail display
              name: file.name,
              size: file.size,
              type: file.type,
              isImage: file.type.startsWith("image/"),
            },
          ]);
        } else {
          // Chat changed while uploading — revoke the blob to avoid leak
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          console.warn(
            "Upload completed but chat changed; discarding file preview.",
          );
        }
      }
    } catch (error) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      console.error("File upload failed:", error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCancelFile = (index) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => handleFileUpload(file));
  };

  const emitTyping = useCallback(() => {
    if (!socket || !activeChat || !user) return;
    const targetId = activeChat.data._id || activeChat.data.id;

    if (activeChat.type === "dm") {
      socket.emit("typing", {
        senderId: userId,
        receiverId: targetId,
        userName: user.name,
      });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("stop-typing", {
          senderId: userId,
          receiverId: targetId,
          userName: user.name,
        });
      }, 1500);
    } else {
      socket.emit("channel-typing", {
        channelId: targetId,
        senderId: userId,
        userName: user.name,
      });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("channel-stop-typing", {
          channelId: targetId,
          senderId: userId,
          userName: user.name,
        });
      }, 1500);
    }
  }, [socket, activeChat, user, userId]);

  useEffect(() => {
    return () => {
      clearTimeout(typingTimeoutRef.current);
      if (!socket || !activeChat || !user) return;
      const targetId = activeChat.data._id || activeChat.data.id;
      if (activeChat.type === "dm") {
        socket.emit("stop-typing", {
          senderId: userId,
          receiverId: targetId,
          userName: user.name,
        });
      } else {
        socket.emit("channel-stop-typing", {
          channelId: targetId,
          senderId: userId,
          userName: user.name,
        });
      }
    };
  }, [activeChat]);

  useEffect(() => {
    if (chatId && updateChatDraftStatus) {
      // Drive the sidebar draft dot off ACTUAL composer content, not the
      // hasUnsavedChanges flag (which flips true on programmatic loads/switches
      // and lit up every channel even when nothing was typed).
      updateChatDraftStatus(chatId, !!value.trim());
    }
  }, [chatId, value, updateChatDraftStatus]);

  useEffect(() => {
    return () => {
      // Revoke all blob preview URLs to prevent memory leaks
      setPendingFiles((prev) => {
        prev.forEach((f) => {
          if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
        });
        return [];
      });
    };
  }, [activeChat]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => handleFileUpload(file));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const fileItems = Array.from(items).filter((item) => item.kind === "file");

    if (fileItems.length === 0) return;

    e.preventDefault(); // only blocks default when files are present; plain text paste unaffected

    fileItems.forEach((item) => {
      const file = item.getAsFile();
      if (file) handleFileUpload(file);
    });
  };

  useEffect(() => {
    if (showScheduler && scheduledAt) {
      try {
        const date = new Date(scheduledAt);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          setScheduledDate(`${year}-${month}-${day}`);
          setScheduledTime(`${hours}:${minutes}`);
        }
      } catch (e) {
        console.warn("Invalid scheduledAt format:", scheduledAt);
      }
    }
  }, [showScheduler, scheduledAt]);

  const handleScheduleChange = useCallback(() => {
    if (scheduledDate && scheduledTime) {
      const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (!isNaN(dateTime.getTime())) {
        setScheduled(dateTime.toISOString());
      }
    } else {
      setScheduled(null);
    }
  }, [scheduledDate, scheduledTime, setScheduled]);

  useEffect(() => {
    handleScheduleChange();
  }, [scheduledDate, scheduledTime, handleScheduleChange]);

  return (
    <div className="flex-shrink-0 px-3 md:px-6 pb-4 md:pb-6 pt-2">
      {showLinkModal && (
        <LinkModal
          selectedText={selectedText}
          onInsert={handleLinkInsert}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {showScheduler && (
        <div className="mb-3 p-3 md:p-4 bg-app-surface border border-border-subtle animate-fade-in flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-ink/[0.06] border border-border-subtle flex items-center justify-center flex-shrink-0">
              <AppIcon name="ClockIcon" size={18} className="text-ink" />
            </div>
            <div>
              <p className="eyebrow eyebrow-12 !text-ink">Schedule Message</p>
              <p className="text-[11px] text-text-muted hidden sm:block mt-0.5">
                Will be sent automatically at the chosen time
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="flex-1 sm:flex-none bg-app-bg border border-border-subtle px-2 md:px-3 py-2 text-[12px] text-text-primary outline-none focus:border-ink"
            />
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="flex-1 sm:flex-none bg-app-bg border border-border-subtle px-2 md:px-3 py-2 text-[12px] text-text-primary outline-none focus:border-ink"
            />
            <button
              onClick={() => setShowScheduler(false)}
              className="p-2 text-text-muted hover:text-text-primary transition-colors flex-shrink-0"
            >
              <AppIcon name="XMarkIcon" size={16} />
            </button>
          </div>
        </div>
      )}

      <div
        className={`relative flex flex-col border transition-colors duration-200 ${
          dragOver
            ? "border-ink bg-ink/[0.03] ring-2 ring-ink/10"
            : focused
              ? "bg-app-surface border-ink ring-2 ring-ink/10"
              : "bg-app-surface border-border-subtle"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <button
          onClick={() => scrollToBottom?.("smooth")}
          aria-label="Scroll to bottom"
          className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-app-surface border border-border-subtle shadow-premium text-text-muted hover:text-text-primary hover:border-primary/30 transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
            isNearBottom
              ? "opacity-0 scale-75 pointer-events-none"
              : "opacity-100 scale-100"
          }`}
        >
          <AppIcon name="ChevronDownIcon" size={18} />
        </button>

        <div className="flex items-center gap-0.5 md:gap-1 px-2 pt-2 pb-1 border-b border-border-subtle/50 overflow-x-auto">
          <ToolbarButton
            icon="CodeBracketIcon"
            active={isCode}
            onClick={() => setIsCode(!isCode)}
            title="Code block"
          />
          <div className="w-px h-4 bg-border-subtle mx-0.5 md:mx-1 flex-shrink-0" />

          {/* Link */}
          <ToolbarButton
            icon="LinkIcon"
            onClick={handleLinkClick}
            title="Insert link"
            className="hidden sm:flex"
          />

          {/* Bullet List */}
          <ToolbarButton
            icon="ListBulletIcon"
            onClick={handleBulletList}
            title="Bullet list"
            className="hidden sm:flex"
          />

          <div className="w-px h-4 bg-white/10 mx-0.5 md:mx-1 flex-shrink-0 hidden sm:block" />

          <ToolbarButton
            icon="PaperClipIcon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={isUploading ? "animate-pulse" : ""}
            title="Attach file"
          />

          <div className="relative flex-shrink-0">
            <ToolbarButton
              ref={emojiBtnRef}
              icon="FaceSmileIcon"
              active={showEmojiPicker}
              onClick={() => setShowEmojiPicker((p) => !p)}
              title="Emoji picker"
              aria-label="Open emoji picker"
            />
            {showEmojiPicker && (
              <EmojiPicker
                anchorRef={emojiBtnRef}
                pickerRef={emojiPickerRef}
                onSelect={handleEmojiSelect}
                onClose={() => setShowEmojiPicker(false)}
              />
            )}
          </div>

          <ToolbarButton
            icon="AtSymbolIcon"
            onClick={handleAtClick}
            title="Mention someone"
            className="hidden sm:flex"
          />
        </div>

        {mentionQuery !== null &&
          (channelMembers.length > 0 || activeChat?.type === "channel") && (
            <div className="relative">
              <MentionDropdown
                filteredMembers={filteredMembers}
                onSelect={handleMentionSelect}
                dropdownRef={mentionDropdownRef}
                selectedIndex={mentionSelectedIndex}
                itemRefs={mentionItemRefs}
                channelMembers={channelMembers}
              />
            </div>
          )}

        {pendingFiles.length > 0 && (
          <AttachmentPreviewGrid
            files={pendingFiles}
            onRemove={handleCancelFile}
          />
        )}

        <textarea
          ref={inputRef}
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={
            isCode
              ? "Paste your code snippet here..."
              : `Message ${channelName ? `#${channelName}` : "conversation"}...`
          }
          className={`w-full bg-transparent px-4 py-4 text-[15px] text-text-primary resize-none outline-none placeholder:text-text-muted custom-scrollbar ${isCode ? "font-mono bg-black/5" : ""}`}
          rows={Math.min(10, value.split("\n").length || 1)}
          disabled={isUploading}
        />

        <DraftIndicator
          lastSaved={lastSaved}
          hasUnsavedChanges={hasUnsavedChanges}
          isLoading={draftLoading}
        />

        <div className="flex items-center justify-between px-3 pb-3 pt-1">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowScheduler(!showScheduler)}
              className={`flex items-center gap-2 px-2 md:px-3 py-2 text-[11px] font-mono uppercase tracking-[0.1em] font-medium transition-colors ${
                showScheduler
                  ? "bg-ink text-white"
                  : "text-text-muted hover:bg-black/5 hover:text-text-primary"
              }`}
            >
              <AppIcon name="CalendarDaysIcon" size={14} />
              <span className="hidden sm:inline">Schedule</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {isUploading && (
              <span className="eyebrow eyebrow-10 animate-pulse mr-1 hidden sm:inline">
                Uploading…
              </span>
            )}
            <button
              onClick={() => handleSend()}
              disabled={
                !(value.trim() || pendingFiles.length > 0) ||
                isUploading ||
                isScheduling
              }
              className={`h-9 md:h-10 px-4 md:px-6 text-[13px] font-semibold flex items-center gap-2 transition-colors active:scale-95 ${
                (value.trim() || pendingFiles.length > 0) &&
                !isUploading &&
                !isScheduling
                  ? "bg-ink text-white hover:bg-ink/90"
                  : "bg-black/5 text-text-muted cursor-not-allowed"
              }`}
            >
              <span className="hidden sm:inline">Send</span>
              <AppIcon
                name="PaperAirplaneIcon"
                size={14}
                className={
                  value.trim() || pendingFiles.length > 0
                    ? "translate-x-0.5 -translate-y-0.5"
                    : "opacity-40"
                }
              />
            </button>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileChange}
      />
    </div>
  );
}

const ToolbarButton = React.forwardRef(function ToolbarButton(
  {
    icon,
    onClick,
    active,
    disabled,
    className = "",
    title,
    "aria-label": ariaLabel,
  },
  ref,
) {
  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel || title}
      className={`p-1.5 h-8 w-8 flex items-center justify-center rounded-lg transition-all duration-200 ${
        active
          ? "bg-primary/10 text-primary"
          : "text-text-muted hover:bg-bg-muted hover:text-text-primary"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
    >
      <AppIcon name={icon} size={15} />
    </button>
  );
});
